/* UI state (Zustand): theme, density, dev "IDs" mode, sidebar collapse, palette. Persisted per browser
   (convenience only; wrapped so blocked storage never breaks the app). */
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

export type Theme = 'system' | 'light' | 'dark';
export type Density = 'simple' | 'full';

export interface UiState {
  theme: Theme;
  density: Density;
  ids: boolean;
  navCollapsed: boolean;
  paletteOpen: boolean;
  moreOpen: boolean;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setIds: (v: boolean) => void;
  toggleNav: () => void;
  setPalette: (v: boolean) => void;
  setMore: (v: boolean) => void;
}

const safeStorage: StateStorage = {
  getItem: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  setItem: (k, v) => { try { localStorage.setItem(k, v); } catch { /* blocked */ } },
  removeItem: (k) => { try { localStorage.removeItem(k); } catch { /* blocked */ } },
};

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      density: 'simple',
      ids: false,
      navCollapsed: false,
      paletteOpen: false,
      moreOpen: false,
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setIds: (ids) => set({ ids }),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
      setPalette: (paletteOpen) => set({ paletteOpen }),
      setMore: (moreOpen) => set({ moreOpen }),
    }),
    {
      name: 'cx.ui',
      storage: createJSONStorage(() => safeStorage),
      // A stale `demo` key from older builds is simply ignored.
      partialize: (s) => ({ theme: s.theme, density: s.density, ids: s.ids, navCollapsed: s.navCollapsed }),
    },
  ),
);

/** Mirrors the UI state onto <html> attributes that the CSS reads. */
export function applyRootAttributes(s: Pick<UiState, 'theme' | 'density' | 'ids'>, root: HTMLElement = document.documentElement): void {
  if (s.theme === 'system') delete root.dataset.theme; else root.dataset.theme = s.theme;
  root.dataset.density = s.density;
  root.dataset.ids = s.ids ? 'on' : 'off';
  const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  root.dataset.pointer = coarse ? 'coarse' : 'fine';
}
